<template>
  <div v-on-clickaway="closeSearch" class="max-w-5xl w-full relative my-4">
    <public-search-input
      id="search-input"
      v-model="searchTerm"
      :search-placeholder="searchTranslations.searchPlaceholder"
      :handle-enter="handleEnter"
      @focus="openSearch"
    />
    <div
      v-if="shouldShowSearchBox"
      class="absolute top-14 w-full"
      @mouseover="openSearch"
    >
      <search-suggestions
        id="box-search"
        :items="searchResults"
        :is-loading="isLoading"
        :search-term="searchTerm"
        :empty-placeholder="searchTranslations.emptyPlaceholder"
        :results-title="searchTranslations.resultsTitle"
        :loading-placeholder="searchTranslations.loadingPlaceholder"
        :portal-slug="portalSlug"
      />
    </div>
    <div
      v-if="isEnter"
      id="box-search-results"
      :class="isSearchPage ? 'search-page-result' : 'custom-box-result'"
    >
      <!-- Fixed breadcrumb header -->
      <div
        :class="
          isSearchPage ? 'search-page-header' : 'custom-box-result-header'
        "
      >
        <div
          v-if="!isLoading"
          :class="
            isSearchPage ? 'search-page-breadcrumb' : 'custom-box-result-cook'
          "
        >
          <a :href="linkHome()">
            {{ portalSlug.split('-').join(' ') }}
          </a>
          <span> > Resultados de la búsqueda </span>
        </div>
      </div>

      <!-- Scrollable content -->
      <div
        :class="
          isSearchPage ? 'search-page-content' : 'custom-box-result-content'
        "
      >
        <div
          v-if="isLoading"
          :class="
            isSearchPage ? 'search-page-loading' : 'custom-box-result-loading'
          "
        >
          {{ searchTranslations.loadingPlaceholder }}
        </div>
        <div
          v-if="searchResults.length === 0 && !isLoading"
          :class="
            isSearchPage ? 'search-page-empty' : 'custom-box-result-empty'
          "
        >
          <h4>No hay resultados para "{{ searchTerm }}"</h4>
        </div>

        <div
          v-if="searchResults.length > 0 && !isLoading"
          :class="isSearchPage ? 'search-page-main' : 'custom-box-result-main'"
        >
          <!-- Card de resumen de la búsqueda -->
          <div
            v-if="summary && summary !== ''"
            id="search-summary-card"
            class="search-summary-card"
          >
            <div id="summary-header-container" class="summary-header">
              <svg
                id="summary-bulb-icon"
                class="summary-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
              <h3 id="summary-main-title" class="summary-title">
                Resumen de la búsqueda
              </h3>
            </div>
            <div id="summary-content-container" class="summary-content">
              <p
                id="summary-text-paragraph"
                v-dompurify-html="summary"
                class="summary-text"
              />
            </div>
          </div>

          <h4>{{ itemTotal }} resultados para "{{ searchTerm }}"</h4>
          <ul
            v-if="searchResults.length > 0 && !isLoading"
            :class="isSearchPage ? 'search-page-list' : 'custom-box-result-ul'"
            role="listbox"
          >
            <li
              v-for="article in searchResults"
              :id="article.id"
              :key="article.id"
              :class="
                isSearchPage ? 'search-page-item' : 'custom-box-result-li'
              "
              role="option"
              tabindex="-1"
            >
              <div class="article-header">
                <div class="article-category">
                  <h4>
                    {{ portalSlug.split('-').join(' ') }} >
                    {{ article.category.name }}
                  </h4>
                </div>
                <div v-if="article.type_search" class="search-type-badge">
                  <span
                    :class="[
                      'badge',
                      article.type_search === 'title'
                        ? 'badge-title'
                        : 'badge-content',
                    ]"
                  >
                    {{
                      article.type_search === 'title' ? 'Título' : 'Contenido'
                    }}
                  </span>
                </div>
              </div>
              <div class="custom-box-result-li-div">
                <a :href="article.link">
                  <span
                    v-dompurify-html="
                      prepareContent(
                        `${article.title} | ${article.category.name}`
                      )
                    "
                    class="article-title"
                  />
                </a>
              </div>
              <div>
                <h3
                  v-dompurify-html="
                    prepareContentFragment(`${article.content}`, article.link)
                  "
                  class="article-content"
                />
              </div>
            </li>
          </ul>
        </div>
      </div>

      <!-- Fixed pagination footer -->
      <div
        v-if="pageTotal > 0"
        :class="
          isSearchPage ? 'search-page-footer' : 'custom-box-result-footer'
        "
      >
        <div class="btn-next">
          <button
            v-if="pageSearch > 1"
            class="btn-pagination btn-prev"
            @click="pageRemove"
          >
            <svg
              class="btn-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span>Anterior</span>
          </button>

          <div class="pagination-info">
            <span>Página {{ pageSearch }} de {{ pageTotal }}</span>
          </div>

          <button
            v-if="pageSearch < pageTotal"
            class="btn-pagination btn-next"
            @click="pageAdd"
          >
            <span>Siguiente</span>
            <svg
              class="btn-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SearchSuggestions from './SearchSuggestions.vue';
import PublicSearchInput from './PublicSearchInput.vue';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import ArticlesAPI from '../api/article';

export default {
  components: {
    PublicSearchInput,
    SearchSuggestions,
  },
  mixins: [messageFormatterMixin],
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
  },
  data() {
    return {
      isEnter: false, // temporal es false
      searchTerm: '',
      isLoading: false,
      showSearchBox: false,
      searchResults: [],
      pageSearch: 0,
      pageTotal: 0,
      summary: '',
      searchResultsTotal: [],
      itemTotal: 0,
      isAutoSearch: false,
    };
  },
  computed: {
    portalSlug() {
      return window.portalConfig.portalSlug;
    },
    localeCode() {
      return window.portalConfig.localeCode;
    },
    shouldShowSearchBox() {
      return this.searchTerm !== '' && this.showSearchBox && !this.isEnter;
    },
    searchTranslations() {
      const { searchTranslations = {} } = window.portalConfig;
      return searchTranslations;
    },
    isHeaderMode() {
      // Verificar si está en modo header basado en la URL
      // URL de home: /hc/[slug]/[locale]
      return !window.location.pathname.match(/\/hc\/[^/]+\/[^/]+$/);
    },
    isSearchPage() {
      // Detectar si estamos en la página de búsqueda /es/search
      return window.location.pathname.includes('/es/search');
    },
  },

  watch: {
    searchTerm() {
      if (!this.isAutoSearch && !this.isSearchPage) {
        this.isEnter = false;
      }

      if (this.typingTimer) {
        clearTimeout(this.typingTimer);
      }
      const box = document.getElementById('box-category');
      if (box && box.classList.contains('hidden')) {
        // Si está presente, quitarla para mostrar el elemento
        box.classList.remove('hidden');
      }

      this.openSearch();
      this.isLoading = true;
      this.typingTimer = setTimeout(() => {
        // En modo header, usamos el límite mayor automáticamente
        this.fetchArticlesByQuery();
      }, 1500);
    },
    currentPage() {
      this.clearSearchTerm();
    },
    isSearchPage: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.isEnter = true;
        }
      },
    },
  },
  async mounted() {
    this.autoExtractQuery();

    // Si estamos en la página de búsqueda, activar isEnter automáticamente
    if (this.isSearchPage) {
      this.isEnter = true;
    }
  },

  beforeDestroy() {
    clearTimeout(this.typingTimer);
  },

  methods: {
    pageAdd() {
      this.pageSearch += 1;
      this.searchResults = this.searchResultsTotal[this.pageSearch - 1];
    },
    pageRemove() {
      this.pageSearch -= 1;
      this.searchResults = this.searchResultsTotal[this.pageSearch - 1];
    },
    splitArray(array, chunkSize) {
      // Initialize an empty array to hold the chunks
      const result = [];
      // Loop through the array and create chunks of the specified size
      for (let i = 0; i < array.length; i += chunkSize) {
        // Slice the array from the current index to the index + chunk size
        const chunk = array.slice(i, i + chunkSize);
        // Push the chunk into the result array
        result.push(chunk);
      }
      return result;
    },
    extractTextFromHTML(html) {
      // Create a temporary DOM element
      const tempDiv = document.createElement('div');

      // Set the HTML string as the innerHTML of the temporary element
      tempDiv.innerHTML = html;

      // Retrieve the text content from the temporary element
      const text = tempDiv.textContent || tempDiv.innerText || '';

      return text;
    },
    showFragment(text, term) {
      const maxChar = 70;
      const textLower = text.toLowerCase();
      const termLower = term.toLowerCase();
      const index = textLower.indexOf(termLower);

      // Si no se encuentra el término, retorna el inicio hasta la última palabra completa
      if (index === -1) {
        const fragment = text.substring(0, maxChar);
        const lastSpaceIndex = fragment.lastIndexOf(' ');
        return lastSpaceIndex === -1
          ? fragment + '...'
          : text.substring(0, lastSpaceIndex) + '...';
      }

      // Calcula los límites iniciales
      let start = Math.max(0, index - maxChar);
      let end = Math.min(text.length, index + term.length + maxChar);

      // Ajusta el inicio para comenzar en una palabra completa
      if (start > 0) {
        start = text.lastIndexOf(' ', start) + 1;
        if (start < 0) start = 0;
      }

      // Ajusta el final para terminar en una palabra completa
      if (end < text.length) {
        const lastSpaceIndex = text.lastIndexOf(' ', end);
        end = lastSpaceIndex === -1 ? end : lastSpaceIndex;
      }

      // Agrega puntos suspensivos al inicio si no empieza desde el principio
      const prefix = start > 0 ? '... ' : '';
      // Agrega puntos suspensivos al final si no termina en el final del texto
      const suffix = end < text.length ? ' ...' : '';

      return prefix + text.substring(start, end) + suffix;
    },
    prepareContent(content) {
      return this.highlightContent(content, this.searchTerm, 'font-bold');
    },
    prepareContentFragment(content, link) {
      const textHtml = this.extractTextFromHTML(content);
      const newContent = this.showFragment(textHtml, this.searchTerm);
      return (
        this.highlightContent(newContent, this.searchTerm, 'font-bold') +
        `
      <a href="${link}"><span class="custom-box-result-li-a">
                    Leer más
                  </span></a>
      `
      );
    },
    linkHome() {
      return `/hc/${this.portalSlug}`;
    },
    async handleEnter() {
      this.hideContent404();
      // Validar si ya hay un fetch en proceso
      this.isEnter = true;

      this.isLoading = true;
      await this.fetchArticlesByQuery(1000, true); // Pasar true para modo enter (summary)
      if (this.searchResults.length > 10) {
        this.searchResultsTotal = this.splitArray(this.searchResults, 10);
        this.searchResults = this.searchResultsTotal[0];
        this.pageSearch = 1;
        this.pageTotal = this.searchResultsTotal.length;
      }
      const boxCategory = document.getElementById('box-category');
      if (boxCategory) {
        boxCategory.classList.add('hidden');
      }
      this.isLoading = false;
      const authSection = document.getElementById('auth-section');
      if (authSection) {
        authSection.classList.add('hidden');
      }
      const portalBgArticle = document.getElementById('portal-bg-article');
      if (portalBgArticle) {
        portalBgArticle.classList.add('hidden');
      }

      // Aquí puedes añadir la lógica adicional que necesites al presionar Enter.
    },
    onChange(e) {
      this.$emit('input', e.target.value);
    },
    onBlur(e) {
      this.$emit('blur', e.target.value);
    },
    openSearch() {
      this.showSearchBox = true;
    },
    closeSearch() {
      // No cerrar la búsqueda si estamos en la página de búsqueda
      if (!this.isSearchPage) {
        this.showSearchBox = false;
      }
    },
    clearSearchTerm() {
      this.searchTerm = '';
    },
    parseTitle(title = '') {
      return title
        .toString()
        .toLowerCase()
        .normalize('NFD') // descompone acentos (e.g. á → a + ́)
        .replace(/[\u0300-\u036f]/g, '') // elimina los diacríticos
        .replace(/ñ/g, 'n') // reemplazo explícito por si normalize no lo cubre
        .replace(/[^a-z0-9\s]/g, ''); // elimina todo lo que no sea a-z, 0-9 o espacios
    },
    async fetchArticlesByQuery(limit = null, isEnterMode = false) {
      try {
        this.searchResults = [];
        // Usar el límite pasado como parámetro, o determinar automáticamente
        let resultsLimit;
        if (limit !== null) {
          resultsLimit = limit;
        } else {
          // Si estamos en modo header, traer 1000 resultados, sino 20
          resultsLimit = this.isHeaderMode ? 1000 : 1000;
        }

        const { data } = await ArticlesAPI.searchArticles(
          this.portalSlug,
          this.localeCode,
          this.parseTitle(this.searchTerm),
          false,
          resultsLimit,
          isEnterMode
        );
        this.searchResults = data.payload;
        if (data.summary && data.summary.content) {
          this.summary = data.summary.content;
        } else {
          this.summary = '';
        }
        this.itemTotal = (this.searchResults || []).length;
        this.isLoading = true;
      } catch (error) {
        // Show something wrong message
      } finally {
        this.isLoading = false;
      }
    },
    hideContent404() {
      const content404 = document.getElementById('content-404');
      if (content404) {
        content404.classList.add('hidden');
      }
    },
    autoExtractQuery() {
      const urlParams = new URLSearchParams(window.location.search);
      const query = urlParams.get('query');
      if (query) {
        this.searchTerm = query;
        this.isAutoSearch = true;
        this.isEnter = true;
        this.hideContent404();
      }

      // Si estamos en la página de búsqueda pero no hay query, mantener isEnter activo
      if (this.isSearchPage && !query) {
        this.isEnter = true;
      }
    },
  },
};
</script>
<style lang="css">
.custom-box-result {
  background-color: white !important;
  margin-top: 2rem !important;
  width: 100% !important;
  max-height: 80vh !important;
  border-radius: 8px !important;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid #e5e7eb !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}

/* Fixed header with breadcrumb */
.custom-box-result-header {
  position: sticky !important;
  top: 0 !important;
  z-index: 10 !important;
  background-color: white !important;
  flex-shrink: 0 !important;
}

.custom-box-result-cook {
  padding: 1rem 0.5rem 0.5rem 0.5rem !important;
  margin: 0 !important;
  text-align: left !important;
}

.custom-box-result-cook a {
  text-decoration: none !important;
  outline: none !important;
  cursor: pointer !important;
  color: #1f73b7 !important;
  font-weight: 600;
}

.custom-box-result-cook span {
  color: #6b7280 !important;
  font-size: 0.875rem !important;
}

/* Scrollable content area */
.custom-box-result-content {
  flex: 1 !important;
  overflow-y: auto !important;
  width: 100% !important;
  min-height: 0 !important;
}

.custom-box-result-loading {
  text-align: center !important;
  padding: 3rem 1rem !important;
  font-size: 1.25rem !important;
  font-weight: 600;
  color: #00377d !important;
}

.custom-box-result-empty {
  padding: 2rem 1rem !important;
  text-align: center !important;
}

.custom-box-result-empty h4 {
  font-size: 1.125rem !important;
  font-weight: 600;
  color: #00377d !important;
  margin: 0 !important;
}

.custom-box-result-main {
  padding: 1rem 0.5rem !important;
}

/* Search Summary Card */
.search-summary-card {
  background: #ffffff !important;
  border: 2px solid #00377d !important;
  border-radius: 12px !important;
  margin: 0 0.5rem 1.5rem 0.5rem !important;
  overflow: hidden !important;
  box-shadow: 0 4px 12px rgba(0, 55, 125, 0.15) !important;
}

.summary-header {
  background: linear-gradient(135deg, #00377d 0%, #1f73b7 100%) !important;
  padding: 0.875rem 1rem !important;
  border-bottom: none !important;
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
}

.summary-icon {
  width: 1.25rem !important;
  height: 1.25rem !important;
  color: white !important;
  flex-shrink: 0 !important;
}

.summary-title {
  font-size: 0.9375rem !important;
  font-weight: 600 !important;
  color: white !important;
  margin: 0 !important;
}

.summary-content {
  padding: 1rem !important;
}

.search-summary-card .summary-content .summary-text,
.search-summary-card .summary-content p {
  font-size: 0.875rem !important;
  line-height: 1.6 !important;
  color: #000000 !important;
  margin: 0 !important;
  font-weight: 700 !important;
  text-shadow: none !important;
}

/* Forzar el color negro en el texto del resumen */
#search-summary-card .summary-content * {
  color: #000000 !important;
  font-weight: 700 !important;
}

.custom-box-result-main h4 {
  font-size: 1.125rem !important;
  font-weight: 600;
  color: #00377d !important;
  margin: 0 0 1rem 0 !important;
  padding: 0 0.5rem !important;
}

.hidden {
  display: none;
}

.custom-box-result-ul {
  background: #fff !important;
  padding: 0 0.5rem 1rem 0.5rem !important;
  margin: 0 !important;
  list-style: none !important;
}

.custom-box-result-li {
  padding: 1.5rem 0 !important;
  border-bottom: 1px solid rgba(0, 55, 125, 0.15) !important;
  margin: 0 !important;
}

.custom-box-result-li:last-child {
  border-bottom: none !important;
}

.article-header {
  display: flex !important;
  justify-content: space-between !important;
  align-items: flex-start !important;
  margin-bottom: 0.75rem !important;
  gap: 1rem !important;
}

.article-category {
  flex: 1 !important;
}

.custom-box-result-li h4 {
  background: rgba(0, 55, 125, 0.11) !important;
  padding: 0.25rem 0.5rem !important;
  border-radius: 4px !important;
  display: inline-block !important;
  color: #666 !important;
  font-size: 0.75rem !important;
  margin: 0 !important;
  font-weight: 500 !important;
}

.search-type-badge {
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
  flex-shrink: 0 !important;
}

.badge {
  padding: 0.25rem 0.5rem !important;
  border-radius: 12px !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.025em !important;
}

.badge-title {
  background-color: #10b981 !important;
  color: white !important;
}

.badge-content {
  background-color: #3b82f6 !important;
  color: white !important;
}

.relevance-score {
  display: flex !important;
  align-items: center !important;
  background-color: #f3f4f6 !important;
  border-radius: 8px !important;
  padding: 0.25rem 0.5rem !important;
}

.score-text {
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  color: #374151 !important;
}

.article-title {
  font-weight: 600 !important;
  font-size: 1rem !important;
  line-height: 1.4 !important;
}

.article-content {
  color: #4b5563 !important;
  font-size: 0.875rem !important;
  line-height: 1.5 !important;
  margin: 0.5rem 0 0 0 !important;
  font-weight: 400 !important;
}

.custom-box-result-li a {
  font-weight: 500 !important;
  color: #00377d !important;
  text-decoration: none !important;
  outline: none !important;
  cursor: pointer !important;
  line-height: 1.5 !important;
}

.custom-box-result-li a:hover {
  color: #1f73b7 !important;
  text-decoration: underline !important;
}

.custom-box-result-li-div {
  margin: 0.5rem 0 !important;
}

.custom-box-result-li-a {
  color: #00c8ff !important;
  text-decoration: none !important;
  outline: none !important;
  cursor: pointer !important;
  font-size: 0.875rem !important;
  font-weight: 500 !important;
}

.custom-box-result-li-a:hover {
  color: #198afd !important;
  text-decoration: underline !important;
}

/* Fixed footer with pagination */
.custom-box-result-footer {
  position: sticky !important;
  bottom: 0 !important;
  z-index: 10 !important;
  background-color: white !important;
  flex-shrink: 0 !important;
}

.btn-next {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  padding: 0.75rem 0.5rem !important;
  gap: 1rem !important;
}

.btn-pagination {
  display: flex !important;
  align-items: center !important;
  gap: 0.375rem !important;
  background-color: transparent !important;
  color: #374151 !important;
  padding: 0.5rem 0.75rem !important;
  border: none !important;
  border-radius: 4px !important;
  font-size: 0.8125rem !important;
  font-weight: 500 !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  min-width: 80px !important;
}

.btn-pagination:hover {
  background-color: #f3f4f6 !important;
  color: #111827 !important;
}

.btn-pagination:active {
  background-color: #e5e7eb !important;
}

.btn-pagination:disabled {
  background-color: transparent !important;
  color: #9ca3af !important;
  cursor: not-allowed !important;
}

.btn-icon {
  width: 0.875rem !important;
  height: 0.875rem !important;
  flex-shrink: 0 !important;
}

.pagination-info {
  display: flex !important;
  align-items: center !important;
  font-size: 0.8125rem !important;
  color: #6b7280 !important;
  font-weight: 500 !important;
  min-width: 100px !important;
  justify-content: center !important;
}

/* Responsive design */
@media (max-width: 768px) {
  .custom-box-result {
    margin-top: 1rem !important;
    max-height: 70vh !important;
  }

  .custom-box-result-main h4 {
    font-size: 1rem !important;
    padding: 0 0.25rem !important;
  }

  .search-summary-card {
    margin: 0 0.25rem 1rem 0.25rem !important;
  }

  .summary-header {
    padding: 0.75rem 0.875rem !important;
  }

  .summary-title {
    font-size: 0.875rem !important;
  }

  .summary-content {
    padding: 0.875rem !important;
  }

  .search-summary-card .summary-content .summary-text {
    font-size: 0.8125rem !important;
    color: #000000 !important;
    font-weight: 700 !important;
    text-shadow: none !important;
  }

  .custom-box-result-ul {
    padding: 0 0.25rem 1rem 0.25rem !important;
  }

  .custom-box-result-li {
    padding: 1rem 0 !important;
  }

  .article-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.5rem !important;
  }

  .search-type-badge {
    align-self: flex-end !important;
  }

  .badge {
    font-size: 0.6875rem !important;
    padding: 0.1875rem 0.375rem !important;
  }

  .relevance-score {
    padding: 0.1875rem 0.375rem !important;
  }

  .score-text {
    font-size: 0.6875rem !important;
  }

  .article-title {
    font-size: 0.9375rem !important;
  }

  .article-content {
    font-size: 0.8125rem !important;
  }

  .btn-next {
    flex-direction: column !important;
    gap: 0.75rem !important;
    padding: 1rem 0.25rem !important;
  }

  .btn-pagination {
    width: 100% !important;
    max-width: 200px !important;
    justify-content: center !important;
  }

  .pagination-info {
    order: -1 !important;
    min-width: auto !important;
  }
}

@media (max-width: 480px) {
  .custom-box-result {
    max-height: 60vh !important;
  }

  .custom-box-result-main h4 {
    font-size: 0.875rem !important;
  }

  .search-summary-card {
    margin: 0 0.25rem 0.875rem 0.25rem !important;
    border-radius: 8px !important;
  }

  .summary-header {
    padding: 0.625rem 0.75rem !important;
  }

  .summary-icon {
    width: 1rem !important;
    height: 1rem !important;
  }

  .summary-title {
    font-size: 0.8125rem !important;
  }

  .summary-content {
    padding: 0.75rem !important;
  }

  .search-summary-card .summary-content .summary-text {
    font-size: 0.75rem !important;
    line-height: 1.5 !important;
    color: #000000 !important;
    font-weight: 700 !important;
    text-shadow: none !important;
  }

  .custom-box-result-li h4 {
    font-size: 0.625rem !important;
  }

  .article-header {
    gap: 0.375rem !important;
  }

  .badge {
    font-size: 0.625rem !important;
    padding: 0.125rem 0.25rem !important;
  }

  .relevance-score {
    padding: 0.125rem 0.25rem !important;
  }

  .score-text {
    font-size: 0.625rem !important;
  }

  .article-title {
    font-size: 0.875rem !important;
  }

  .article-content {
    font-size: 0.75rem !important;
  }

  .custom-box-result-cook {
    padding: 0.75rem 0.25rem 0.5rem 0.25rem !important;
  }

  .btn-next {
    padding: 0.75rem 0.25rem !important;
  }

  .btn-pagination {
    padding: 0.625rem 0.75rem !important;
    font-size: 0.8125rem !important;
  }
}

/* Estilos para la página de búsqueda /es/search - Más adaptado y fluido */
.search-page-result {
  background-color: transparent !important;
  margin-top: 1rem !important;
  width: 100% !important;
  max-height: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  border: none !important;
  display: block !important;
  overflow: visible !important;
}

/* Header más simple para la página de búsqueda */
.search-page-header {
  position: static !important;
  background-color: transparent !important;
  margin-bottom: 1rem !important;
}

.search-page-breadcrumb {
  padding: 0.5rem 0 !important;
  margin: 0 !important;
  text-align: left !important;
  border-bottom: 1px solid #e5e7eb !important;
}

.search-page-breadcrumb a {
  text-decoration: none !important;
  outline: none !important;
  cursor: pointer !important;
  color: #1f73b7 !important;
  font-weight: 600;
}

.search-page-breadcrumb span {
  color: #6b7280 !important;
  font-size: 0.875rem !important;
}

/* Contenido más fluido */
.search-page-content {
  width: 100% !important;
  overflow: visible !important;
  min-height: auto !important;
}

.search-page-loading {
  text-align: center !important;
  padding: 2rem 0 !important;
  font-size: 1.125rem !important;
  font-weight: 600;
  color: #00377d !important;
}

.search-page-empty {
  padding: 2rem 0 !important;
  text-align: center !important;
}

.search-page-empty h4 {
  font-size: 1.125rem !important;
  font-weight: 600;
  color: #00377d !important;
  margin: 0 !important;
}

.search-page-main {
  padding: 0 !important;
}

.search-page-main h4 {
  font-size: 1.25rem !important;
  font-weight: 600;
  color: #00377d !important;
  margin: 0 0 1.5rem 0 !important;
  padding: 0 !important;
}

/* Lista más natural sin bordes rígidos */
.search-page-list {
  background: transparent !important;
  padding: 0 !important;
  margin: 0 !important;
  list-style: none !important;
}

/* Items de artículo más espaciados y naturales */
.search-page-item {
  padding: 2rem 0 !important;
  border-bottom: 1px solid rgba(0, 55, 125, 0.1) !important;
  margin: 0 !important;
  background: transparent !important;
}

.search-page-item:last-child {
  border-bottom: none !important;
}

.search-page-item:hover {
  background-color: rgba(0, 55, 125, 0.02) !important;
  transition: background-color 0.2s ease !important;
}

.search-page-item .article-header {
  display: flex !important;
  justify-content: space-between !important;
  align-items: flex-start !important;
  margin-bottom: 1rem !important;
  gap: 1rem !important;
}

.search-page-item .article-category {
  flex: 1 !important;
}

.search-page-item h4 {
  background: rgba(0, 55, 125, 0.08) !important;
  padding: 0.375rem 0.75rem !important;
  border-radius: 6px !important;
  display: inline-block !important;
  color: #00377d !important;
  font-size: 0.8125rem !important;
  margin: 0 !important;
  font-weight: 600 !important;
}

.search-page-item .article-title {
  font-weight: 700 !important;
  font-size: 1.125rem !important;
  line-height: 1.4 !important;
  color: #00377d !important;
}

.search-page-item .article-content {
  color: #4b5563 !important;
  font-size: 0.9375rem !important;
  line-height: 1.6 !important;
  margin: 0.75rem 0 0 0 !important;
  font-weight: 400 !important;
}

.search-page-item a {
  font-weight: 500 !important;
  color: #00377d !important;
  text-decoration: none !important;
  outline: none !important;
  cursor: pointer !important;
  line-height: 1.5 !important;
  transition: color 0.2s ease !important;
}

.search-page-item a:hover {
  color: #1f73b7 !important;
  text-decoration: underline !important;
}

/* Footer de paginación más natural */
.search-page-footer {
  position: static !important;
  background-color: transparent !important;
  margin-top: 2rem !important;
  border-top: 1px solid #e5e7eb !important;
  padding-top: 1rem !important;
}

.search-page-footer .btn-next {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  padding: 1rem 0 !important;
  gap: 1rem !important;
}

.search-page-footer .btn-pagination {
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
  background-color: #f8f9fa !important;
  color: #00377d !important;
  padding: 0.75rem 1rem !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 6px !important;
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  min-width: 100px !important;
}

.search-page-footer .btn-pagination:hover {
  background-color: #00377d !important;
  color: white !important;
  border-color: #00377d !important;
}

.search-page-footer .pagination-info {
  display: flex !important;
  align-items: center !important;
  font-size: 0.875rem !important;
  color: #6b7280 !important;
  font-weight: 500 !important;
  min-width: 120px !important;
  justify-content: center !important;
}

/* Responsive para página de búsqueda */
@media (max-width: 768px) {
  .search-page-item {
    padding: 1.5rem 0 !important;
  }

  .search-page-item .article-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.75rem !important;
  }

  .search-page-item .article-title {
    font-size: 1rem !important;
  }

  .search-page-item .article-content {
    font-size: 0.875rem !important;
  }

  .search-page-footer .btn-next {
    flex-direction: column !important;
    gap: 1rem !important;
  }

  .search-page-footer .pagination-info {
    order: -1 !important;
  }
}

@media (max-width: 480px) {
  .search-page-main h4 {
    font-size: 1.125rem !important;
  }

  .search-page-item {
    padding: 1.25rem 0 !important;
  }

  .search-page-item .article-title {
    font-size: 0.9375rem !important;
  }

  .search-page-item .article-content {
    font-size: 0.8125rem !important;
  }

  .search-page-footer .btn-pagination {
    padding: 0.625rem 0.875rem !important;
    min-width: 90px !important;
    font-size: 0.8125rem !important;
  }
}
</style>
